import { useState, useEffect } from 'react';
import { Mail, Phone, Trash2, Download, Check, Eye } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminContacts() {
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedContact, setSelectedContact] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [page]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/admin/contacts`, {
        params: { page, limit: 20 },
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data.items);
      setTotalPages(response.data.pages);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (contactId) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/contacts/${contactId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchContacts();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/admin/contacts/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Contact deleted');
      setDeleteId(null);
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleViewContact = (contact) => {
    setSelectedContact(contact);
    if (!contact.is_read) {
      handleMarkRead(contact.id);
    }
  };

  const handleExport = () => {
    window.open(`${API_URL}/api/admin/export/contacts`, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-contacts">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#2D3E55]">
              Contact Messages
            </h1>
            <p className="font-body text-slate-600 mt-1">
              View and manage contact form submissions
            </p>
          </div>
          <Button onClick={handleExport} variant="outline" data-testid="export-csv-btn">
            <Download size={18} className="mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Contacts List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-slate-200 rounded w-1/4"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : contacts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="font-body text-slate-500">No contact messages yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {contacts.map((contact) => (
              <Card 
                key={contact.id} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${!contact.is_read ? 'border-l-4 border-l-[#F15A24]' : ''}`}
                onClick={() => handleViewContact(contact)}
                data-testid={`contact-${contact.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-body font-semibold text-[#2D3E55]">
                          {contact.full_name}
                        </span>
                        {!contact.is_read && (
                          <Badge className="bg-[#F15A24]">New</Badge>
                        )}
                      </div>
                      <p className="font-body text-lg text-[#2D3E55] mb-2">
                        {contact.subject}
                      </p>
                      <p className="font-body text-slate-600 text-sm line-clamp-2 mb-3">
                        {contact.message}
                      </p>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {contact.email}
                        </span>
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {contact.phone}
                          </span>
                        )}
                        <span>{formatDate(contact.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewContact(contact);
                        }}
                      >
                        <Eye size={16} className="mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteId(contact.id);
                        }}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <span className="flex items-center px-4 font-body text-slate-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </Button>
          </div>
        )}

        {/* View Contact Modal */}
        <Dialog open={!!selectedContact} onOpenChange={() => setSelectedContact(null)}>
          <DialogContent className="max-w-2xl">
            {selectedContact && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl text-[#2D3E55]">
                    {selectedContact.subject}
                  </DialogTitle>
                  <DialogDescription>
                    From: {selectedContact.full_name} • {formatDate(selectedContact.created_at)}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="flex flex-wrap gap-4 text-sm">
                    <a href={`mailto:${selectedContact.email}`} className="flex items-center gap-2 text-[#F15A24] hover:underline">
                      <Mail size={16} />
                      {selectedContact.email}
                    </a>
                    {selectedContact.phone && (
                      <a href={`tel:${selectedContact.phone}`} className="flex items-center gap-2 text-[#2D3E55] hover:underline">
                        <Phone size={16} />
                        {selectedContact.phone}
                      </a>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-body text-slate-700 whitespace-pre-wrap">
                      {selectedContact.message}
                    </p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Contact</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this contact message? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
